/* ============================================================
   ÉKO '77 — HOME: LOCATIONS

   Port of the three locations loops in the reference assets/js/site.js:

     1. THE STAGE. One restaurant is always the active one, so the framed
        print on the right is never orphaned. Pointing at (or tabbing to)
        a row lights its name and cross-fades its photograph in.
     2. THE TILT. The print grows and turns as the band travels past —
        a page being laid into a scrapbook. --stage-s / --stage-r are the
        reference's own curves, read by assets/x-locations.css.
     3. THE OPENING STATUS. "Open now · until 10pm" is worked out in the
        restaurants' own timezone, in the VISITOR's browser, because a
        server-rendered hour would be wrong for half the day and a cached
        page would be wrong for all of it.

   Reference maths, kept exactly:
     tilt t = clamp((vh - rect.top) / (vh + rect.height), 0, 1)   // 0…1
     scale  = 0.94 + t * 0.12
     rotate = -3.2 + t * 4.6 deg

   Added on top of the reference, because a Shopify theme needs it:
     • the pointer/focus handlers are DELEGATED on the document, so a
       section re-rendered by the theme editor needs no re-binding at all;
     • the tilt is re-collected on shopify:section:load / :unload /
       :reorder and the reduced-motion preference is watched live;
     • every user-facing string, the day names and the timezone arrive
       from section settings as data-attributes, so the status line is
       translatable and editable without touching this file;
     • the status is recomputed when a backgrounded tab comes back, so a
       page left open through closing time does not keep saying "Open now".

   The script is idempotent: it installs exactly one set of listeners no
   matter how many times a page requests it.
   ============================================================ */
(function () {
  'use strict';

  if (window.__xLocations) return;
  window.__xLocations = true;

  var MARGIN = 100; // px of slack before the band is considered offscreen
  var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  var stages = [];
  var ticking = false;
  var listening = false;

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  /* ---------- 1. the stage ---------- */

  function select(row) {
    var band = row.closest('.x-locations');
    if (!band) return;

    var key = row.getAttribute('data-shot');

    band.querySelectorAll('.x-loc[data-shot]').forEach(function (other) {
      other.classList.toggle('is-active', other === row);
    });

    band.querySelectorAll('.x-loc__shot[data-shot]').forEach(function (shot) {
      shot.classList.toggle('is-on', shot.getAttribute('data-shot') === key);
    });
  }

  function onPoint(event) {
    var row = event.target.closest ? event.target.closest('.x-loc[data-shot]') : null;
    if (row) select(row);
  }

  /* Delegated, and on the document rather than per row: mouseenter does not
     bubble, so the reference binds one listener per <li>. mouseover does
     bubble, which means one listener covers every band on the page AND every
     row the theme editor adds later. focusin bubbles too, so a keyboard
     visitor drives the same stage. */
  document.addEventListener('mouseover', onPoint);
  document.addEventListener('focusin', onPoint);

  /* ---------- 2. the tilt ---------- */

  function collect() {
    stages = Array.prototype.slice.call(document.querySelectorAll('.x-locations__stage'));
  }

  function reset() {
    stages.forEach(function (stage) {
      stage.style.removeProperty('--stage-s');
      stage.style.removeProperty('--stage-r');
    });
  }

  function tilt() {
    ticking = false;
    if (mq.matches) return;

    var vh = window.innerHeight;

    stages.forEach(function (stage) {
      var band = stage.closest('.x-locations');
      if (!band) return;

      var rect = band.getBoundingClientRect();
      if (rect.bottom < -MARGIN || rect.top > vh + MARGIN) return; // offscreen, skip

      var t = clamp((vh - rect.top) / (vh + rect.height), 0, 1);
      stage.style.setProperty('--stage-s', (0.94 + t * 0.12).toFixed(3));
      stage.style.setProperty('--stage-r', (-3.2 + t * 4.6).toFixed(2) + 'deg');
    });
  }

  function onTilt() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tilt);
  }

  function listen() {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', onTilt, { passive: true });
    window.addEventListener('resize', onTilt, { passive: true });
  }

  /* ---------- 3. the opening status ---------- */

  var DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  function nowIn(tz) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz || 'Europe/London',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(new Date());
    } catch (e) {
      // an unknown timezone must not take the whole band down with it
      parts = new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(new Date());
    }

    var got = {};
    parts.forEach(function (part) {
      got[part.type] = part.value;
    });

    // 24:00 is how en-GB spells midnight at the start of a day
    var hour = parseInt(got.hour, 10) % 24;
    return { day: DAY_INDEX[got.weekday], mins: hour * 60 + parseInt(got.minute, 10) };
  }

  /* British clock: 13 → 1pm, 22 → 10pm, 12 → midday, 0 → midnight */
  function spellHour(hour, band) {
    if (hour === 0 || hour === 24) return band.getAttribute('data-midnight') || 'midnight';
    if (hour === 12) return band.getAttribute('data-midday') || 'midday';
    return (hour % 12) + (hour < 12 ? 'am' : 'pm');
  }

  function fill(template, time, day) {
    return String(template || '')
      .replace('[time]', time == null ? '' : time)
      .replace('[day]', day == null ? '' : day);
  }

  function status(row) {
    var line = row.querySelector('.x-loc__status');
    if (!line) return;

    var band = row.closest('.x-locations');
    if (!band) return;

    var hours;
    try {
      hours = JSON.parse(row.getAttribute('data-hours'));
    } catch (e) {
      return; // no usable hours — leave the line empty rather than guess
    }
    if (!hours || typeof hours.open !== 'number') return;

    var days = (band.getAttribute('data-days') || '').split(',').map(function (name) {
      return name.trim();
    });

    var now = nowIn(band.getAttribute('data-tz'));
    var closeToday = Array.isArray(hours.close) ? hours.close[now.day] : hours.close;
    var openMin = hours.open * 60;
    var opensAt = spellHour(hours.open, band);

    if (closeToday != null && now.mins >= openMin && now.mins < closeToday * 60) {
      line.className = 'x-loc__status is-open';
      line.textContent = fill(
        band.getAttribute('data-open-now'),
        spellHour(closeToday, band)
      );
      return;
    }

    line.className = 'x-loc__status';

    if (closeToday != null && now.mins < openMin) {
      line.textContent = fill(band.getAttribute('data-opens-today'), opensAt);
      return;
    }

    // walk forward to the next day the room actually opens
    var next = null;
    for (var step = 1; step <= 7; step++) {
      var day = (now.day + step) % 7;
      var close = Array.isArray(hours.close) ? hours.close[day] : hours.close;
      if (close != null) {
        next = { day: day, step: step };
        break;
      }
    }

    if (!next) {
      line.textContent = band.getAttribute('data-closed') || '';
    } else if (next.step === 1) {
      line.textContent = fill(band.getAttribute('data-opens-tomorrow'), opensAt);
    } else {
      line.textContent = fill(
        band.getAttribute('data-opens-day'),
        opensAt,
        days[next.day] || ''
      );
    }
  }

  function stamp() {
    document.querySelectorAll('.x-locations[data-status] .x-loc[data-hours]').forEach(status);
  }

  /* ---------- wiring ---------- */

  function refresh() {
    stamp();

    // one row is always active, so the stage is never blank
    document.querySelectorAll('.x-locations').forEach(function (band) {
      if (band.querySelector('.x-loc.is-active')) return;
      var first = band.querySelector('.x-loc[data-shot]');
      if (first) select(first);
    });

    collect();
    if (!stages.length) return;
    if (mq.matches) {
      reset();
      return;
    }
    listen();
    tilt();
  }

  function onPreferenceChange() {
    if (mq.matches) reset();
    else refresh();
  }

  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', onPreferenceChange);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(onPreferenceChange);
  }

  // a tab left open through closing time must not keep saying "Open now"
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) stamp();
  });

  // theme editor: a re-rendered section brings new rows and drops old ones
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(
    function (evt) {
      document.addEventListener(evt, refresh);
    }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
